import { Component } from '@theme/component';
import { DialogCloseEvent, DialogComponent } from '@theme/dialog';
import { CartUpdateEvent, ThemeEvents } from '@theme/events';
import { morph } from '@theme/morph';

/**
 * Custom Quick Add Component
 * Handles the quick add button and modal functionality
 */
export class CustomQuickAddComponent extends Component {
  /** @type {AbortController | null} */
  #abortController = null;
  /** @type {Document | null} */
  #cachedProductHtml = null;

  get cachedProductHtml() {
    return this.#cachedProductHtml;
  }

  get productPageUrl() {
    return this.dataset.productUrl || '#';
  }

  get productId() {
    return this.dataset.productId;
  }

  get productTitle() {
    return this.dataset.productTitle || '';
  }

  connectedCallback() {
    super.connectedCallback();
    
    // Find the parent product card
    this.productCard = this.closest('product-card');
    
    // Add event delegation for clicks anywhere on the product card
    if (this.productCard) {
      this.productCard.addEventListener('click', this.handleProductCardClick, { passive: false });
    }
  }

  disconnectedCallback() {
    this.#abortController?.abort();
    
    // Clean up event listeners
    const button = this.querySelector('.custom-quick-add__overlay-button');
    if (button) {
      button.removeEventListener('click', this.handleClick);
    }
    
    if (this.productCard) {
      this.productCard.removeEventListener('click', this.handleProductCardClick);
    }
    
    super.disconnectedCallback();
  }



  /**
   * Handles clicks on the product title to open quick add modal
   * @param {Event} event - The click event
   */
  handleProductCardClick = async (event) => {
    const target = /** @type {Element} */ (event.target);
    
    if (!target || typeof target.closest !== 'function') {
      return;
    }
    
    // Only handle clicks on the product title
    let isOnTitle = false;
    try {
      isOnTitle = !!target.closest('.custom-product-card-title');
    } catch (e) {
      // Ignore selector errors
      return;
    }
    
    if (isOnTitle) {
      event.preventDefault();
      event.stopPropagation();
      await this.#openModal();
    }
  };

  /**
   * Handles quick add button click (legacy method, kept for compatibility)
   * @param {Event} event - The click event
   */
  handleClick = async (event) => {
    event.preventDefault();
    event.stopPropagation();
    await this.#openModal();
  };

  /**
   * Opens the quick add modal (unified method)
   */
  async #openModal() {
    // Show loading state
    this.setAttribute('loading', 'true');

    try {
      if (!this.#cachedProductHtml) {
        await this.fetchProductPage(this.productPageUrl);
      }

      if (this.#cachedProductHtml) {
        // Create a fresh copy of the cached HTML to avoid modifying the original
        const freshHtmlCopy = new DOMParser().parseFromString(
          this.#cachedProductHtml.documentElement.outerHTML,
          'text/html'
        );

        await this.updateQuickAddModal(freshHtmlCopy);
      }

      this.#openQuickAddModal();
    } catch (error) {
      // console.error('Error opening quick add modal:', error);
    } finally {
      this.removeAttribute('loading');
    }
  }

  /**
   * Opens the custom quick add modal
   */
  #openQuickAddModal = () => {
    const dialogComponent = document.getElementById('custom-quick-add-dialog');
    
    if (dialogComponent instanceof CustomQuickAddDialog) {
      this.#stayVisibleUntilDialogCloses(dialogComponent);
      dialogComponent.showDialog();
    } else {
      // console.error('Custom quick add dialog not found');
    }
  };

  /** @param {CustomQuickAddDialog} dialogComponent */
  #stayVisibleUntilDialogCloses(dialogComponent) {
    this.toggleAttribute('stay-visible', true);

    dialogComponent.addEventListener(
      DialogCloseEvent.eventName, 
      () => this.toggleAttribute('stay-visible', false), 
      { once: true }
    );
  }

  /**
   * Fetches the product page HTML
   * @param {string} productPageUrl - The product page URL
   */
  async fetchProductPage(productPageUrl) {
    if (this.#abortController) {
      this.#abortController.abort();
    }

    this.#abortController = new AbortController();

    try {
      const response = await fetch(productPageUrl, {
        signal: this.#abortController.signal,
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const htmlText = await response.text();
      this.#cachedProductHtml = new DOMParser().parseFromString(htmlText, 'text/html');

      // Reload Judge.me widgets
      setTimeout(function () { jdgmCacheServer.reloadAll(); }, 600);
      
      console.log('jdgmCacheServer.reloadAll() CALLED!')

    } catch (error) {
      if (error.name !== 'AbortError') {
        // console.error('Error fetching product page:', error);
        throw error;
      }
    } finally {
      this.#abortController = null;
    }
  }

  /**
   * Updates the quick add modal content
   * @param {Document} newHtml - The new HTML document
   */
  async updateQuickAddModal(newHtml) {
    const productGrid = newHtml.querySelector('[data-product-grid-content]');
    const modalContent = document.getElementById('custom-quick-add-modal-content');

    if (!productGrid || !modalContent) return;

    const customBuyButtons = productGrid.querySelector('.custom-buy-buttons');
    const modalFooter = document.getElementById('custom-quick-add-modal__footer');
    
    // Clear modal containers first to avoid stale nodes interfering with components
    try {
      if (modalContent) {
        modalContent.innerHTML = '';
      }
      if (modalFooter) {
        modalFooter.innerHTML = '';
      }
    } catch (e) {
      // console.warn('Failed to clear quick add modal containers:', e);
    }

    // Morph footer first (when available), then content
    if (customBuyButtons && modalFooter) {
      morph(modalFooter, customBuyButtons);
    }

    morph(modalContent, productGrid);
  }
}

if (!customElements.get('custom-quick-add-component')) {
  customElements.define('custom-quick-add-component', CustomQuickAddComponent);
}

/**
 * Custom Quick Add Dialog Component
 * Extends the base DialogComponent for modal functionality
 */
export class CustomQuickAddDialog extends DialogComponent {
  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener(ThemeEvents.cartUpdate, this.handleCartUpdate, { signal: this.#abortController.signal });
    this.addEventListener(ThemeEvents.variantUpdate, this.#updateProductTitleLink);
    // Clear modal containers on close to avoid stale content/listeners
    this.addEventListener(DialogCloseEvent.eventName, this.#clearModalContainers, {
      signal: this.#abortController.signal,
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
  }

  /**
   * Closes the dialog
   * @param {CartUpdateEvent} event - The cart update event
   */
  handleCartUpdate = (event) => {
    if (event.detail.data.didError) return;
    
    // Check if the cart update came from within this modal
    const isFromModal = this.#isCartUpdateFromModal(event);
    
    // Only close the dialog if the cart update came from outside the modal
    if (!isFromModal) {
      this.closeDialog();
    }
  };

  /**
   * Checks if the cart update event came from within this modal
   * @param {CartUpdateEvent} event - The cart update event
   * @returns {boolean} - True if the event came from within the modal
   */
  #isCartUpdateFromModal = (event) => {
    // Check if the event source is from a product form within this modal
    const source = event.detail.data?.source;
    if (source === 'product-form-component') {
      // Check if the event target is within this modal
      const modalContent = document.getElementById('custom-quick-add-modal-content');
      const modalFooter = document.getElementById('custom-quick-add-modal__footer');
      
      // Check if the event originated from within the modal content or footer
      const eventTarget = /** @type {Node} */ (event.target);
      if (eventTarget && modalContent && modalFooter) {
        return modalContent.contains(eventTarget) || modalFooter.contains(eventTarget);
      }
    }
    
    return false;
  };

  /**
   * Clears modal content and footer containers
   */
  #clearModalContainers = () => {
    try {
      const modalContent = document.getElementById('custom-quick-add-modal-content');
      const modalFooter = document.getElementById('custom-quick-add-modal__footer');

      if (modalContent) modalContent.innerHTML = '';
      if (modalFooter) modalFooter.innerHTML = '';
    } catch (e) {
      // console.warn('Failed to clear quick add modal containers on close:', e);
    }
  };

  #updateProductTitleLink = (/** @type {CustomEvent} */ event) => {
    const anchorElement = /** @type {HTMLAnchorElement} */ (
      event.detail.data.html?.querySelector('.view-product-title a')
    );
    const viewMoreDetailsLink = /** @type {HTMLAnchorElement} */ (this.querySelector('.view-product-title a'));
    const mobileProductTitle = /** @type {HTMLAnchorElement} */ (this.querySelector('.product-header a'));

    if (!anchorElement) return;

    if (viewMoreDetailsLink) viewMoreDetailsLink.href = anchorElement.href;
    if (mobileProductTitle) mobileProductTitle.href = anchorElement.href;
  };
}

if (!customElements.get('custom-quick-add-dialog')) {
  customElements.define('custom-quick-add-dialog', CustomQuickAddDialog);
}